import {Lang} from 'yougile-lang'
import {
  ArrUtil,
  ffLazyLoading,
  hasFeatureFlag,
  saveAsXlsx,
  Time
} from 'yougile-util'
import {
  DataObjectId,
  getAllFields,
  getFieldsFromReport,
  getReportTasks,
  getStickersFromTasks,
  Hub,
  isEmptyReportColumn,
  noBoardContainerId,
  Report,
  ReportColumn,
  ReportDataField,
  SandboxTree,
  StorageDepartmentHierarchy,
  TaskData
} from 'yougile-model'
import {getLazyReportTasks} from '@client/lazy-load'
import {Sync} from '@client/sync'
import {idTasksApi} from '@extensions/id-tasks/id-tasks-store'
import {makeTaskDataService} from '@extensions/task-data-helper'
import {DepartmentsClient} from '../../../client/departments-client'
import {BoardFilters} from '../../../extensions/sticker/board-filters'
import {getAllStickers} from '../../../extensions/sticker/sticker-data-extractor'
import {Task} from '../../../extensions/task'
import {taskDataFromHub} from '../../../extensions/task-data-extractor'

const notInArchivedProject = (task: Hub, box: SandboxTree) => {
  if (!task || task.location === noBoardContainerId) {
    return true
  }
  const col = box.get(task.location) as Hub
  if (col) {
    const b = box.get(col.location) as Hub
    if (b) {
      const proj = box.get(b.location) as Hub
      if (proj?.complete) {
        return false
      }
    }
  }
  return true
}

export const downloadDashboardXlsx = async (
  report: Report,
  includeAllFields = false
) => {
  const box = Sync.getCurrentSandbox()
  const tasks = await asyncGetAllReportTasks(box, report)
  const allStickers = getAllStickers(box, true)
  const allFields = getFieldsFromReport(
    report.columns[0],
    getStickersFromTasks(tasks, allStickers, report.columns[0].fields),
    includeAllFields
  )
  const now = Time.now()
  const data: Array<Array<string | number>> = []
  const header = allFields.map(f => f(Lang.msg).getName())
  const timeTrackingCellKeys = ['timetrackingPlan', 'timetrackingWork']
  const showSumTitle = allFields.some(field => {
    const cellField = field(Lang.msg)
    return (
      cellField.getType?.() === 'numeric' ||
      timeTrackingCellKeys.includes(cellField.key)
    )
  })
  data.push(header)
  const taskDataService = makeTaskDataService(box)
  tasks.forEach(t => {
    data.push(allFields.map(f => f(Lang.msg).getText(t, taskDataService)))
  })

  if (showSumTitle) {
    const sumCells = allFields.map((f, ix) => {
      const name = f(Lang.msg).getType?.()
      const key = f(Lang.msg).key

      const stickerIsNumeric = name === 'numeric'
      const stickerIsTimeTracking = timeTrackingCellKeys.includes(key)

      const stickerId = stickerIsNumeric ? key.split('sticker:')[1] : ''

      const sum =
        stickerIsNumeric || stickerIsTimeTracking
          ? tasks.reduce((acc, task) => {
              if (stickerIsTimeTracking) {
                const taskDataService = makeTaskDataService(Sync.get(task))
                return acc + Number(f(Lang.msg).getText(task, taskDataService))
              }
              const taskStickerValue = task.stickers[stickerId]
              return taskStickerValue
                ? Number((acc + Number(taskStickerValue)).toFixed(4))
                : acc
            }, 0)
          : undefined

      if (ix === 0) {
        return sum !== undefined
          ? `${Lang.msg.sumOfValues}: ${sum}`
          : Lang.msg.sumOfValues
      }

      return sum || ''
    })

    data.push(Array(sumCells.length))
    data.push(sumCells)
  }
  saveAsXlsx(report.title + ' ' + Time.formatDateTime(now) + '.xlsx', data)
}

export const asyncGetAllReportTasks = async (
  ...args: Parameters<typeof getAllReportTasks>
): Promise<TaskData[]> => {
  if (hasFeatureFlag(ffLazyLoading)) {
    const [box, report] = args
    const tasksIds = await getLazyReportTasks(report.id)
    const tasksHubs = tasksIds.map(id => box.getHub(id)).filter(Boolean)

    const taskDataMap = tasksHubs.reduce(
      (acc, item) => {
        if (!item) {
          return acc
        }
        acc[item.id] = true
        return acc
      },
      {} as {[key: DataObjectId]: boolean}
    )

    const filteredHubs: Hub[] = []

    for (const taskHub of tasksHubs) {
      if (!taskHub) {
        continue
      }
      if (taskHub.data.mirrors) {
        const firstMirror = getFirstAvailableMirror(
          taskHub.data.mirrors,
          taskDataMap
        )
        if (firstMirror === taskHub.id) {
          filteredHubs.push(taskHub)
        }
      } else {
        filteredHubs.push(taskHub)
      }
    }

    const tasks = filteredHubs.map(hub => taskDataFromHub(box, hub))

    return tasks
  }
  return getAllReportTasks(...args)
}

export const getAllReportTasks = (
  box: SandboxTree,
  report: Report,
  onlyForTasks?: TaskData[],
  filterByStickersForBoard?: Hub,
  hierarchy?: StorageDepartmentHierarchy,
  allTaskHubs?: Hub[]
): TaskData[] => {
  if (hasFeatureFlag(ffLazyLoading)) {
    return []
  }
  const allTasks = report.columns.flatMap((column, i) =>
    getTasks(
      box,
      column,
      onlyForTasks,
      filterByStickersForBoard,
      i,
      hierarchy,
      allTaskHubs
    )
  )

  allTasks.forEach(task => {
    task.idTask = idTasksApi.getIdTask(task.id)
  })

  // TODO! it's a crutch:
  //  For now "sort" property placed only in the first column.
  //  each column is a "filter group".
  //  thus without that code -> sorting of the first column would be applied only for tasks from the first filter group
  //  but the expected result is that all tasks will be sorted (not only the tasks in first column = first filter group)
  const sorting = report.columns[0].sort
  const allStickers = getAllStickers(box, false)
  const allFields = getAllFields(getStickersFromTasks(allTasks, allStickers))
  const sort: {field: ReportDataField; asc: boolean}[] = []
  for (const s of sorting ?? []) {
    const field = allFields.find(f => f(Lang.msg).key === s.key)
    if (field) {
      sort.push({field, asc: s.asc})
    }
  }
  sort.forEach(s => {
    allTasks.sort((a, b) => s.field(Lang.msg).order(a, b, !s.asc))
  })

  return ArrUtil.unique(allTasks, x => x.id)
}

export const getTasks = (
  box: SandboxTree,
  column: ReportColumn,
  onlyForTasks?: TaskData[],
  filterByStickersForBoard?: Hub,
  columnIndex?: number,
  hierarchy?: StorageDepartmentHierarchy,
  allTaskHubs?: Hub[]
): TaskData[] => {
  if (!!columnIndex && isEmptyReportColumn(column)) {
    return []
  }
  hierarchy ??= DepartmentsClient.getHierarchy(box)
  allTaskHubs ??= Task.allTasks(box.companyId, hierarchy)
  let taskHubs = onlyForTasks
    ? []
    : allTaskHubs.filter(t => !t.data.private && notInArchivedProject(t, box))

  if (filterByStickersForBoard && !onlyForTasks) {
    taskHubs = BoardFilters.filter(filterByStickersForBoard, taskHubs)
  }

  const allTasks = onlyForTasks ?? taskHubs.map(h => taskDataFromHub(box, h))
  const allStickers = getAllStickers(box, false)

  const taskDataService = makeTaskDataService(box)
  const taskDataList = getReportTasks(
    column,
    allTasks,
    allStickers,
    taskDataService
  )

  const taskDataMap = taskDataList.reduce(
    (acc, item) => {
      acc[item.id] = true
      return acc
    },
    {} as {[key: DataObjectId]: boolean}
  )

  const output: TaskData[] = []

  for (const taskData of taskDataList) {
    if (taskData.mirrors) {
      const firstMirror = getFirstAvailableMirror(taskData.mirrors, taskDataMap)
      if (firstMirror === taskData.id) {
        output.push(taskData)
      }
    } else {
      output.push(taskData)
    }
  }

  return output
}

const getFirstAvailableMirror = (
  mirrors: DataObjectId[],
  map: {[key: DataObjectId]: boolean}
): DataObjectId | undefined => {
  for (const mirror of mirrors) {
    if (map[mirror]) {
      return mirror
    }
  }

  return undefined
}
